import Fuse, { type IFuseOptions } from 'fuse.js';

/**
 * Configuration for creating a fuzzy search function.
 *
 * @typeParam T - The type of items being searched.
 */
export interface FuseSearchConfig<T> {
  /** Fuse.js key/weight pairs defining which fields to search and their relative importance. */
  keys: IFuseOptions<T>['keys'];
  /** Maximum score threshold (0 = exact match, 1 = match anything). Defaults to 0.4. */
  threshold?: number;
}

const DEFAULT_THRESHOLD = 0.4;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Creates a cached fuzzy search function powered by Fuse.js.
 *
 * The Fuse index is rebuilt only when the `items` array changes by reference.
 * Subsequent searches against the same array reuse the cached index,
 * avoiding O(n) index construction on every keystroke. The cache expires
 * after 5 minutes to free memory when search is idle.
 *
 * @typeParam T - The type of items being searched.
 * @param config - Search configuration specifying which fields to search and their weights.
 * @returns A function that filters and ranks items against a search string,
 *   returning matching items sorted by relevance or the original array when search is empty.
 *
 * @example
 * const searchItems = createFuseSearch<Product>({
 *   keys: [{ name: 'name', weight: 2 }, { name: 'sku', weight: 1 }],
 * });
 * const results = searchItems(products, 'chicken');
 */
export const createFuseSearch = <T>(config: FuseSearchConfig<T>) => {
  const fuseOptions: IFuseOptions<T> = {
    findAllMatches: true,
    ignoreLocation: true,
    keys: config.keys,
    shouldSort: true,
    threshold: config.threshold ?? DEFAULT_THRESHOLD,
    useExtendedSearch: true,
  };

  let cachedItems: T[] | null = null;
  let cachedFuse: Fuse<T> | null = null;
  let cachedAt = 0;

  return (items: T[], search: string): T[] => {
    const trimmed = search.trim();

    // Free memory if cache has expired
    if (cachedAt && Date.now() - cachedAt > CACHE_TTL_MS) {
      cachedItems = null;
      cachedFuse = null;
      cachedAt = 0;
    }

    if (!trimmed) {
      return items;
    }

    if (cachedItems !== items || !cachedFuse) {
      cachedFuse = new Fuse(items, fuseOptions);
      cachedItems = items;
      cachedAt = Date.now();
    }

    return cachedFuse.search(trimmed).map((result) => result.item);
  };
};
